import { useMemo, useState } from "react";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import DatePicker from "@rio-cloud/rio-uikit/components/datepicker/DatePicker";
import Select from "@rio-cloud/rio-uikit/components/selects/Select";
import { IChildForm } from "../../../";
import { Moment } from "moment";

import SearchAutocomplete from "../../../../../components/form/searchAutocomplete";
import CopyButton from "./CopyButton";

import { Asset, AssetModel } from "../../../../../models/asset";
import { Occurrence } from "../../../../../models/occurrence";
import { getAssetModelToSelectOption } from "../../../../../utils/assetUtils";

import { fetchAssetMetrics, fetchComplementaryAssetsDetails } from "../../../../../api/fetchAssets";
import { fetchMaintenance } from "../../../../../api/fetchMaintenance";
import { checkChassisAvailability } from "../../../../../api/occurrence";
import { fetchOccurenceList } from "../../../../../api/fetchOccurence";
import { fetchContractByVehicle } from "../../../../../api/fetchContract";
//import { fetchSchedulingByChassisList } from "../../../../../api/fetchScheduling";
import { extractContractModality, extractContractNumber } from "../../../../../utils/contractUtils";

import ErrorDialog from "../../../../dialogs/ErrorDialog";
import LoadingDialog from "../../../../dialogs/LoadingDialog";

import useAssets from "../../../../../hooks/useAssets";
import useAssetModels from "../../../../../hooks/useAssetModels";

const VehicleChildForm = (props: IChildForm) => {
  const { getFilteredAssets } = useAssets();
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<{
    title: string;
    body: string;
  } | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    body: string;
  } | null>(null);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleError, setVehicleError] = useState<string>('');
  const [volkstotalData, setVolkstotalData] = useState<{
    contractNumber: string;
    modality: string;
  } | null>(null);

  const { assetModels } = useAssetModels();

  return (
    <>
      {/* Loading Modal para busca de veículo */}
      {vehicleLoading && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-700 font-medium">Carregando informações do veículo...</p>
          </div>
        </div>
      )}

      <div className="w-full my-6 p-6 pb-0 border rounded">
        <div
          className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : ""
            }`}
        >
        {!props.hiddenFields.includes("assetId") && (
          <>
          <div
            className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[2]"} ${props.hasError && props.requiredFields.includes("assetId")
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="assetId" className="truncate">
              Buscar veículo
            </label>
            <SearchAutocomplete
              id="assetId"
              placeholder="Buscar veículo pelo nome, chassi ou placa"
              fetchOptions={async (searchTerm) => {
                const response = getFilteredAssets(searchTerm, 50, 0);
                return response;
              }}
              optionLabel={{
                name: "Nome",
                chassis: "Chassi",
                licensePlate: "Placa",
              }}
              optionRenderer={(asset: Asset) => (
                <div>
                  <strong>Nome:</strong> {asset.name || "-"}&nbsp;&nbsp;
                  <strong>Chassi:</strong> {asset.chassis || "-"}&nbsp;&nbsp;
                  <strong>Placa:</strong> {asset.licensePlate || "-"}
                </div>
              )}
              validateOption={async (asset: Asset) => {
                setShowLoadingDialog(true);
                setLoadingMessage({
                  title: "Verificando disponibilidade",
                  body: "Aguarde um momento enquanto verificamos se o chassi informado está disponível para uso.",
                });

                // Check if the chassis is available for use in dynamics
                const isChassisAvailable = await checkChassisAvailability(asset.chassis);
                if (!isChassisAvailable) {
                  setShowErrorDialog(true);
                  setErrorMessage({
                    title: "Chassi inválido",
                    body: "O chassi informado não está disponível para uso.",
                  });
                  setShowLoadingDialog(false);
                  return false;
                }

                // Check if the chassis has a occurrence in progress
                const isOccurrenceInProgress = await fetchOccurenceList(
                  { chassisListFilter: [asset.chassis] },
                  props.persona ?? "TOWER",
                  0,
                  1,
                  false,
                );
                if (isOccurrenceInProgress.occurrences.length > 0) {
                  setShowErrorDialog(true);
                  setErrorMessage({
                    title: "Chassi já em uso",
                    body: "Já existe um chamado em andamento para este chassi.",
                  });
                  setShowLoadingDialog(false);
                  return false;
                }

                setShowLoadingDialog(false);
                return isChassisAvailable;
              }}
              onSelectedOption={async (asset: Asset) => {
                try {
                  setVehicleLoading(true);
                  setVehicleError('');
                  setVolkstotalData(null);
                  
                  

                  // Encontrar o modelId correspondente ao modelCode do asset
                  const modelObj = assetModels.find((model) => model.modelCode === asset.modelCode);
                  let occurrenceData: Partial<Occurrence> = {
                    vehicleChassis: asset.chassis,
                    vehicleModel: modelObj ? modelObj.modelId : undefined,
                    vehicleModelCode: asset.modelCode,
                    vehicleName: asset.name,
                    vehicleYear: asset.year,
                    vehicleLicensePlate: asset.licensePlate,
                  };
                  props.setOccurrence && props.setOccurrence(occurrenceData);

                  const assetMetrics = await fetchAssetMetrics([asset.id]);
                  if (assetMetrics.length > 0) {
                    occurrenceData = {
                      ...occurrenceData,
                      vehicleOdometer: assetMetrics[0].odometer,
                      vehicleHourmeter: assetMetrics[0].hourmeter,
                      vehicleId: asset.id,
                      vehicleType: asset.type,
                    };
                  }

                  const complementaryAssetsDetails = await fetchComplementaryAssetsDetails([asset.id]);
                  const model = complementaryAssetsDetails[0].model ?? '';
                  
                  // Armazenar dados do volkstotal
                  if (complementaryAssetsDetails[0].volkstotal?.contractNumber && complementaryAssetsDetails[0].volkstotal?.modality) {
                    setVolkstotalData({
                      contractNumber: complementaryAssetsDetails[0].volkstotal.contractNumber,
                      modality: complementaryAssetsDetails[0].volkstotal.modality,
                    });
                  } else {
                    setVolkstotalData(null);
                  }
                  
                  occurrenceData = {
                    ...occurrenceData,
                    customer: complementaryAssetsDetails[0].accountName ?? '-',
                    accountName: complementaryAssetsDetails[0].accountName ?? '-',
                    vehicleModelCode: complementaryAssetsDetails[0].modelCode ?? occurrenceData.vehicleModelCode,
                    vehicleModel: complementaryAssetsDetails[0].vehicleModel ?? occurrenceData.vehicleModel ?? model,
                    model: model,
                    vehicleSaleDate: complementaryAssetsDetails[0].saleDate ? new Date(complementaryAssetsDetails[0].saleDate) : undefined,
                  }

                  props.setOccurrence && props.setOccurrence(occurrenceData);
                } catch (error: any) {
                  console.error('Erro ao buscar informações do veículo:', error);
                  setVehicleError('Erro ao carregar informações do veículo. Tente novamente.');
                } finally {
                  setVehicleLoading(false);
                }
              }}
              overlay
              overlayClassName="!w-[50%]"
              disabled={props.disabledAllFields || props.disabledFields.includes("assetId")}
            />

            {/* Mensagem de erro para veículos com agendamento existente */}
            {vehicleError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <div className="text-red-700 font-medium" style={{ fontSize: '16px' }}>
                      <p style={{ whiteSpace: 'pre-line' }}>{vehicleError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {!props.hiddenFields.includes("assetId") && (
            <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[0.8]"}`}>
              <label htmlFor="volkstotalContract" className="truncate">
                Contrato Volks|Total
              </label>
              <div className="relative">
                <input
                  id="volkstotalContract"
                  type="text"
                  className="form-control pr-10 text-color-primary font-bold"
                  placeholder="Contrato Volks|Total"
                  disabled={true}
                  value={volkstotalData ? `${volkstotalData.contractNumber} - ${volkstotalData.modality}` : ''}
                />
                {volkstotalData && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <CopyButton textToCopy={`${volkstotalData.contractNumber} - ${volkstotalData.modality}`} />
                  </div>
                )}
              </div>
            </div>
          )}
          </>
        )}
        </div>
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : ""
          }`}
      >
      {props.hiddenFields.includes("occurrenceType") ||
          props.persona === "MANAGER" ? null : (
          <div
            className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[0.3]"} ${props.hasError &&
              props.requiredFields.includes("occurrenceType") &&
              !props.occurrence?.occurrenceType
              ? "has-error"
              : ""
              }`}
          >
            <label htmlFor="occurrenceType" className="truncate">
              Tipo de ocorrência
              {props.requiredFields.includes("occurrenceType") ? " *" : ""}
            </label>
            <Select
              id="occurrenceType"
              className="w-full"
              options={[
                { id: "undefined", label: "Selecione o tipo de ocorrência" },
                { id: "P", label: "Preventiva" },
                { id: "C", label: "Corretiva" },
                { id: "PC", label: "Preventiva e Corretiva" },
                { id: "A", label: "Socorro" },
                { id: "W", label: "Guincho" },
                { id: "D", label: "Veículo na Concessionária" },
              ]
                .filter((option) => {
                  if (
                    ["P", "PC", "C"].includes(option.id) &&
                    ["P", "PC", "C"].includes(props.occurrence?.occurrenceType ?? "")
                  ) return true;

                  else if (
                    ["A", "W", "D"].includes(option.id)
                  ) return true;

                  return false;
                })}
              value={[props.occurrence?.occurrenceType ?? "undefined"]}
              onChange={(e) => {
                if (e && e.id !== "undefined") {
                  props.occurrence &&
                    props.setOccurrenceValue("occurrenceType", e.id);
                } else {
                  props.occurrence &&
                    props.setOccurrenceValue("occurrenceType", undefined);
                }
              }}
              disabled={
                ["P", "PC", "C"].includes(props.occurrence?.occurrenceType ?? "") ||
                props.disabledAllFields ||
                props.disabledFields.includes("occurrenceType")
              }
            />
          </div>
        )}
      {!props.hiddenFields.includes("vehicleChassis") && (
          <div
            className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[0.35]"} ${props.hasError &&
              props.requiredFields.includes("vehicleChassis") &&
              !props.occurrence?.vehicleChassis
              ? "has-error"
              : ""
            }`}
            
        >
            <label htmlFor="vehicleChassis" className="truncate">
              Chassis
              {props.requiredFields.includes("vehicleChassis") ? " *" : ""}
            </label>
            <div className={`flex gap-4 ${isMobile() ? "flex-wrap" : "items-center"}`}>
              <div className="relative flex-1">
                <input
                  id="vehicleChassis"
                  type="text"
                  className="form-control pr-10"
                  placeholder="Chassi"
                  disabled={true}
                  value={props.occurrence?.vehicleChassis}
                  onChange={(e) =>
                    props.occurrence &&
                    props.setOccurrenceValue("vehicleChassis", e.target.value)
                  }
                  maxLength={17}
                />
                {props.occurrence?.vehicleChassis && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <CopyButton textToCopy={props.occurrence.vehicleChassis} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!props.hiddenFields.includes("vehicleModel") && (
          <div
            className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[0.25]"} ${props.hasError &&
                props.requiredFields.includes("vehicleModel") &&
                !props.occurrence?.vehicleModel && !props.occurrence?.model
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleModel" className="truncate">
              Modelo do veículo
              {props.requiredFields.includes("vehicleModel") ? " *" : ""}
            </label>
            {(() => {
              const modelFromCode = props.occurrence?.vehicleModelCode 
                ? assetModels.find((m) => m.modelCode === props.occurrence?.vehicleModelCode)
                : undefined;
              
              const modelName = props.occurrence?.model || modelFromCode?.description;
              
              if (modelFromCode && !props.occurrence?.vehicleModel) {
                props.setOccurrenceValue("vehicleModel", modelFromCode.modelId);
                props.setOccurrenceValue("model", modelFromCode.description);
              }
              
              if (modelName) {
                return (
                  <input
                    id="vehicleModel"
                    type="text"
                    className="form-control"
                    disabled={true}
                    value={modelName}
                  />
                );
              }
              
              return (
                <SearchAutocomplete
                  id="vehicleModel"
                  placeholder="Modelo do veículo"
                  fetchOptions={async (searchTerm) => assetModels
                    .filter((model) => model.modelId && model.description && model.description.toLowerCase().includes(searchTerm?.toLowerCase()))
                  }
                  optionLabel={{
                    modelCode: "Código",
                    description: "Modelo",
                  }}
                  optionRenderer={(model: AssetModel) => (
                    <div>
                      <strong>Código:</strong> {model.modelCode} - <strong>Modelo:</strong> {model.description ?? "-"}
                    </div>
                  )}
                  onSelectedOption={(model: AssetModel) => {
                    props.occurrence &&
                      props.setOccurrenceValue("vehicleModel", model.modelId);
                      props.setOccurrenceValue("model", model.description);
                      props.setOccurrenceValue("vehicleModelCode", model.modelCode);
                  }}
                  overlay
                  overlayClassName="!w-[44%]"
                  defaultValue={props.occurrence?.vehicleModel && assetModels.length > 0 ? assetModels.find((model) => model.modelId === props.occurrence?.vehicleModel) : undefined}
                  disabled={props.disabledAllFields || props.disabledFields.includes("vehicleModel")}
                />
              );
            })()}
          </div>
        )}

        {!props.hiddenFields.includes("vehicleCheckup") && props.scheduling && (
          <div className={`form-group form-group-lg w-full`} >
            <label htmlFor="vehicleCheckup" className="truncate">
              Revisão
            </label>
            <input
              id="vehicleCheckup"
              type="text"
              className="form-control"
              disabled={true}
              value={props.scheduling.checkup?.name}
            />
          </div>
        )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : ""
          }`}
      >
        {!props.hiddenFields.includes("vehicleLicensePlate") && (
          <div
            className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[0.25]"} ${props.hasError &&
                props.requiredFields.includes("vehicleLicensePlate") &&
                !props.occurrence?.vehicleLicensePlate
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleLicensePlate" className="truncate">
              Placa do veículo
              {props.requiredFields.includes("vehicleLicensePlate") ? " *" : ""}
            </label>
            <div className="relative">
              <input
                id="vehicleLicensePlate"
                type="text"
                className="form-control pr-10"
                placeholder="Placa do veículo"
                disabled={
                  props.disabledAllFields ||
                  props.disabledFields.includes("vehicleLicensePlate")
                }
                value={props.occurrence?.vehicleLicensePlate ?? ""}
                onChange={(e) =>
                  props.occurrence &&
                  props.setOccurrenceValue("vehicleLicensePlate", e.target.value)
                }
                maxLength={7}
              />
              {props.occurrence?.vehicleLicensePlate && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <CopyButton textToCopy={props.occurrence.vehicleLicensePlate} />
                </div>
              )}
            </div>
          </div>
        )}

        {!props.hiddenFields.includes("vehicleType") && (
          <div
            className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[0.3]"} ${props.hasError &&
                props.requiredFields.includes("vehicleType") &&
                !props.occurrence?.vehicleType
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleType" className="truncate">
              Tipo de veículo
              {props.requiredFields.includes("vehicleType") ? " *" : ""}
            </label>
            <Select
              id="vehicleType"
              options={[
                { label: "Caminhão", id: "truck" },
                { label: "Ônibus", id: "bus" },
                { label: "Van", id: "van" },
                { label: "Carreta", id: "trailer" },
                { label: "Carro", id: "car" },
              ]}
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("vehicleType")
              }
              placeholder="Tipo de veículo"
              className="w-full"
              value={
                props.occurrence?.vehicleType
                  ? [props.occurrence?.vehicleType]
                  : undefined
              }
              onChange={(e) =>
                props.occurrence &&
                props.setOccurrenceValue("vehicleType", e?.id ?? null)
              }
            />
          </div>
        )}

        {!props.hiddenFields.includes("vehicleName") && (
          <div
            className={`form-group form-group-lg ${isMobile() ? "w-full" : "flex-[0.45]"} ${props.hasError &&
                props.requiredFields.includes("vehicleName") &&
                !props.occurrence?.vehicleName
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleName" className="truncate">
              Nome do veículo
              {props.requiredFields.includes("vehicleName") ? " *" : ""}
            </label>
            <div className="relative">
              <input
                id="vehicleName"
                type="text"
                className="form-control pr-10"
                placeholder="Nome do veículo"
                disabled={true}
                value={props.occurrence?.vehicleName}
                onChange={(e) =>
                  props.occurrence &&
                  props.setOccurrenceValue("vehicleName", e.target.value)
                }
                maxLength={100}
              />
              {props.occurrence?.vehicleName && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <CopyButton textToCopy={props.occurrence.vehicleName} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : ""
          }`}
      >
        {!props.hiddenFields.includes("vehicleYear") && (
          <div
            className={`form-group form-group-lg w-full ${props.hasError &&
                props.requiredFields.includes("vehicleYear") &&
                !props.occurrence?.vehicleYear
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleYear" className="truncate">
              Ano{props.requiredFields.includes("vehicleYear") ? " *" : ""}
            </label>
            <input
              id="vehicleYear"
              type="text"
              className="form-control"
              placeholder="Ano"
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("vehicleYear")
              }
              value={props.occurrence?.vehicleYear}
              onChange={(e) => {
                const value = e.target.value || "0";
                if (value === "" || /^[0-9]*$/.test(value)) {
                  props.occurrence &&
                    props.setOccurrenceValue("vehicleYear", parseInt(value));
                }
              }}
              maxLength={4}
            />
          </div>
        )}

        {!props.hiddenFields.includes("vehicleSaleDate") && (
          <div
            className={`form-group form-group-lg w-full ${props.hasError &&
                props.requiredFields.includes("vehicleSaleDate") &&
                !props.occurrence?.vehicleSaleDate
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleSaleDate" className="truncate">
              Data de venda
              {props.requiredFields.includes("vehicleSaleDate") ? " *" : ""}
            </label>
            {props.disabledAllFields ||
              props.disabledFields.includes("vehicleSaleDate") ? (
              <input
                id="vehicleSaleDate"
                type="text"
                className="form-control"
                placeholder="Data de venda"
                disabled={true}
                value={
                  props.occurrence?.vehicleSaleDate
                    ? new Date(
                      props.occurrence?.vehicleSaleDate
                    ).toLocaleDateString("pt-BR")
                    : ""
                }
              />
            ) : (
              <DatePicker
                id="vehicleSaleDate"
                className="pb-0 mb-0"
                value={props.occurrence?.vehicleSaleDate}
                onChange={(result: Moment | string) =>
                  props.occurrence &&
                  props.setOccurrenceValue(
                    "vehicleSaleDate",
                    new Date(result as string)
                  )
                }
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                locale="pt-BR"
              />
            )}
          </div>
        )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : ""
          }`}
      >
        {!props.hiddenFields.includes("vehicleOdometer") && (
          <div
            className={`form-group form-group-lg w-full ${props.hasError &&
                props.requiredFields.includes("vehicleOdometer") &&
                !props.occurrence?.vehicleOdometer
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleOdometer" className="truncate">
              Odômetro
              {props.requiredFields.includes("vehicleOdometer") ? " *" : ""}
            </label>
            <input
              id="vehicleOdometer"
              type="text"
              className="form-control"
              placeholder="Odômetro"
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("vehicleOdometer")
              }
              value={props.occurrence?.vehicleOdometer}
              onChange={(e) => {
                const value = e.target.value || "0";
                if (value === "" || /^[0-9]*$/.test(value)) {
                  props.occurrence &&
                    props.setOccurrenceValue(
                      "vehicleOdometer",
                      parseInt(value)
                    );
                }
              }}
              maxLength={6}
            />
          </div>
        )}

        {!props.hiddenFields.includes("vehicleHourmeter") && (
          <div
            className={`form-group form-group-lg w-full ${props.hasError &&
                props.requiredFields.includes("vehicleHourmeter") &&
                !props.occurrence?.vehicleHourmeter
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="vehicleHourmeter" className="truncate">
              Horímetro
              {props.requiredFields.includes("vehicleHourmeter") ? " *" : ""}
            </label>
            <input
              id="vehicleHourmeter"
              type="text"
              className="form-control"
              placeholder="Horímetro"
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("vehicleHourmeter")
              }
              value={props.occurrence?.vehicleHourmeter}
              onChange={(e) => {
                const value = e.target.value || "0";
                if (value === "" || /^[0-9]*$/.test(value)) {
                  props.occurrence &&
                    props.setOccurrenceValue(
                      "vehicleHourmeter",
                      parseInt(value)
                    );
                }
              }}
              maxLength={6}
            />
          </div>
        )}
      </div>

      

      <div
        className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : ""
          }`}
      >
        {!props.hiddenFields.includes("customer") && (
          <div
            className={`form-group form-group-lg w-full ${props.hasError &&
                props.requiredFields.includes("customer") &&
                !props.occurrence?.customer
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="customer" className="truncate">
              Nome do cliente
              {props.requiredFields.includes("customer") ? " *" : ""}
            </label>
            <input
              id="customer"
              type="text"
              className="form-control"
              placeholder="Nome do cliente"
              disabled={true}
              value={
                props.occurrence?.customer || props.occurrence?.accountName
              }
              onChange={(e) =>
                props.occurrence &&
                props.setOccurrenceValue("customer", e.target.value)
              }
              maxLength={100}
            />
          </div>
        )}

        {/* {!props.hiddenFields.includes("renter") && (
          <div
            className={`form-group form-group-lg w-full ${props.hasError &&
                props.requiredFields.includes("renter") &&
                !props.occurrence?.renter
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="renter" className="truncate">
              Locatário{props.requiredFields.includes("renter") ? " *" : ""}
            </label>
            <input
              id="renter"
              type="text"
              className="form-control"
              placeholder="Locatário"
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("renter")
              }
              value={props.occurrence?.renter}
              onChange={(e) =>
                props.occurrence &&
                props.setOccurrenceValue("renter", e.target.value)
              }
              maxLength={100}
            />
          </div>
        )} */}

        {!props.hiddenFields.includes("emissionStandard") && (
          <div
            className={`form-group form-group-lg w-full ${props.hasError &&
                props.requiredFields.includes("emissionStandard") &&
                !props.occurrence?.emissionStandard
                ? "has-error"
                : ""
              }`}
          >
            <label htmlFor="emissionStandard" className="truncate">
              Legislação
              {props.requiredFields.includes("emissionStandard") ? " *" : ""}
            </label>
            <Select
              id="emissionStandard"
              className="w-full"
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("emissionStandard")
              }
              placeholder="Legislação"
              value={props.occurrence?.emissionStandard ? [props.occurrence?.emissionStandard] : undefined}
              onChange={(e) =>
                props.occurrence &&
                props.setOccurrenceValue("emissionStandard", e?.id ?? null)
              }
              options={[
                { id: "EURO 3", label: "EURO 3" },
                { id: "EURO 5", label: "EURO 5" },
                { id: "EURO 6", label: "EURO 6" },
                { id: "ELETRICO", label: "ELÉTRICO" }
              ]}
            />
             
          </div>
        )}
      </div>

      <ErrorDialog
        show={showErrorDialog}
        title={errorMessage?.title ?? "Erro"}
        body={errorMessage?.body ?? ""}
        onClose={() => setShowErrorDialog(false)}
      />

      <LoadingDialog
        show={showLoadingDialog}
        title={loadingMessage?.title ?? "Carregando"}
        body={loadingMessage?.body ?? ""}
      />
    </>
  );
};

export default VehicleChildForm;
